/**
 * Module context menu. Extracted verbatim (behavior-preserving) from the
 * `{moduleContextMenu && (…)}` block in layout/Sidebar.tsx — renders a mobile
 * bottom sheet or a desktop positioned menu depending on `isMobile`.
 *
 * The orchestrator still owns the `moduleContextMenu` open-state and the
 * `moduleContextMenuRef` used by the click-outside hook; both are threaded in.
 * The render gate stays in the orchestrator so mount/unmount semantics are
 * unchanged. The inline "set delete target" handlers are preserved exactly:
 * they close the menu and stage the module for the delete-confirm dialog.
 */

import type { RefObject } from 'react';
import { ArchiveBoxIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { SidebarModuleItem } from '../types';

interface ModuleContextMenuProps {
  moduleContextMenu: { x: number; y: number; item: SidebarModuleItem };
  moduleContextMenuRef: RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  setModuleContextMenu: (value: null) => void;
  setDeleteModuleTarget: (value: { id: string; name: string }) => void;
  onRename: () => void;
  onArchive: () => void;
}

export function ModuleContextMenu({
  moduleContextMenu,
  moduleContextMenuRef,
  isMobile,
  setModuleContextMenu,
  setDeleteModuleTarget,
  onRename,
  onArchive,
}: ModuleContextMenuProps) {
  return isMobile ? (
    /* Mobile: bottom sheet for module actions */
    <div className="hamburger-sheet-overlay" onClick={() => setModuleContextMenu(null)}>
      <div className="hamburger-sheet" style={{ height: 'auto', maxHeight: '50vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="hamburger-sheet-header">
          <div className="hamburger-sheet-handle" onClick={() => setModuleContextMenu(null)} />
        </div>
        <div className="hamburger-sheet-items">
          <button
            className="hamburger-sheet-item"
            onClick={onRename}
          >
            <PencilSquareIcon style={{ width: 18, height: 18 }} />
            Rename
          </button>
          <button
            className="hamburger-sheet-item"
            onClick={onArchive}
          >
            <ArchiveBoxIcon style={{ width: 18, height: 18 }} />
            Archive
          </button>
          <div className="hamburger-sheet-divider" />
          <button
            className="hamburger-sheet-item hamburger-sheet-item--danger"
            onClick={() => {
              const item = moduleContextMenu.item;
              setModuleContextMenu(null);
              setDeleteModuleTarget({ id: item.module.id, name: item.module.label || item.module.type });
            }}
          >
            <TrashIcon style={{ width: 18, height: 18 }} />
            Delete
          </button>
        </div>
      </div>
    </div>
  ) : (
    /* Desktop: positioned context menu */
    <div
      ref={moduleContextMenuRef}
      className="conversation-context-menu"
      style={{
        position: 'fixed',
        top: moduleContextMenu.y,
        left: moduleContextMenu.x,
        zIndex: 999,
      }}
    >
      <button
        className="context-menu-item"
        onClick={onRename}
      >
        <PencilSquareIcon className="context-menu-item-icon" aria-hidden="true" />
        Rename
      </button>
      <button
        className="context-menu-item"
        onClick={onArchive}
      >
        <ArchiveBoxIcon className="context-menu-item-icon" aria-hidden="true" />
        Archive
      </button>
      <button
        className="context-menu-item context-menu-item-danger"
        onClick={() => {
          const item = moduleContextMenu.item;
          setModuleContextMenu(null);
          setDeleteModuleTarget({ id: item.module.id, name: item.module.label || item.module.type });
        }}
      >
        <TrashIcon className="context-menu-item-icon" aria-hidden="true" />
        Delete
      </button>
    </div>
  );
}
